import { texelToWorld, weightsAtTexel, type SurfaceCoverage, type SurfaceMap } from '@/terrain/TerrainSurface';
import type { ProjectedPoint, Vec3 } from './SceneManifest';

/**
 * How much of the *visible ground* each material covers, measured through a camera.
 *
 * The manifest used to be handed `Terrain.coverage()` — a share of the whole site — and it was the
 * wrong number in two ways at once.
 *
 * It lost features outright. A stream 1.5 m wide across a 46 m site is about 1.5 % of the site,
 * which fell under the manifest's noise floor and was dropped, leaving the prompt asserting
 * "Grass — 98 % of the site" over a frame with a watercourse through the middle of it. The model
 * rendered what it was told, and the stream came back as lawn.
 *
 * And where a material did survive, the figure understated it: ground filling a third of the picture
 * was reported at 3 %, which reads as trim.
 *
 * So ground is measured the way everything else in the manifest is — through the frame. Texels are
 * projected, those outside the frustum are discarded, and the rest are weighted by the screen area
 * they actually occupy: `cos(incidence) / distance²`, the standard foreshortening term. That term is
 * not a refinement. In a grazing view most of the terrain grid lands within a few degrees of the
 * horizon, and counting those texels equally against the foreground would report a garden made
 * almost entirely of whatever is furthest away.
 *
 * Approximate by construction. It samples on a grid and ignores occlusion, so ground behind a
 * building still counts. This is a proportion in a sentence, not a measurement — and it is
 * dramatically closer than the number it replaces.
 *
 * Pure, and separated from `CaptureService` for the reason the rest of this layer is: it takes a
 * projection callback rather than a camera, so the arithmetic is testable without a GPU.
 */

/** Ground samples per axis. Caps the cost so a hectare and a courtyard measure in the same time. */
export const GROUND_SAMPLES_PER_AXIS = 160;

/**
 * Floor on the incidence term.
 *
 * At a perfectly grazing angle the foreshortening factor goes to zero and an entire horizon's worth
 * of ground would weigh nothing at all. A small floor keeps distant ground present but slight, which
 * is what it is in the picture.
 */
export const MIN_GROUND_INCIDENCE = 0.02;

export interface GroundCoverageInput {
  /** The painted surface map. Sampled on a stride, never wholesale. */
  map: SurfaceMap;
  /** Terrain-local metres → world metres, applying the site's north rotation. */
  toWorld: (x: number, z: number) => { x: number; z: number };
  /** Ground height at a terrain-local point. */
  heightAt: (x: number, z: number) => number;
  /** World point → normalised device coordinates, as `SceneManifest` receives it. */
  project: (world: Vec3) => ProjectedPoint;
  /** World position of the camera, for the incidence term. */
  cameraPosition: Vec3;
}

/**
 * Returns shares summing to 1 across the materials visible in frame, or null when no ground is in
 * frame at all — a sky-only or interior view, where saying nothing beats guessing.
 */
export function groundCoverageInView(input: GroundCoverageInput): SurfaceCoverage | null {
  const { map, toWorld, heightAt, project, cameraPosition } = input;
  if (map.width === 0 || map.height === 0) return null;

  const strideX = Math.max(1, Math.ceil(map.width / GROUND_SAMPLES_PER_AXIS));
  const strideZ = Math.max(1, Math.ceil(map.height / GROUND_SAMPLES_PER_AXIS));

  const totals: SurfaceCoverage = {};
  let totalWeight = 0;

  for (let row = 0; row < map.height; row += strideZ) {
    for (let col = 0; col < map.width; col += strideX) {
      const local = texelToWorld(map, col, row);
      const y = heightAt(local.x, local.z);
      const world = toWorld(local.x, local.z);
      const projected = project({ x: world.x, y, z: world.z });

      if (projected.behindCamera) continue;
      if (Math.abs(projected.x) > 1 || Math.abs(projected.y) > 1) continue;

      const distance = Math.max(0.5, projected.distanceMeters);
      // |view · up|: a texel seen edge-on covers almost no pixels.
      const rise = Math.abs(y - cameraPosition.y);
      const incidence = Math.max(MIN_GROUND_INCIDENCE, rise / distance);
      const weight = incidence / (distance * distance);

      for (const [id, value] of Object.entries(weightsAtTexel(map, col, row))) {
        totals[id] = (totals[id] ?? 0) + value * weight;
      }
      totalWeight += weight;
    }
  }

  if (totalWeight <= 0) return null;
  for (const id of Object.keys(totals)) totals[id] = (totals[id] ?? 0) / totalWeight;
  return totals;
}
